import { useCallback, useEffect, useState } from "react";

import { useToast } from "@/components/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";

import { useIsDesktop } from "../../../hooks/useIsDesktop";
import { IntegrationConnectionStatus } from "./IntegrationConnectionStatus";

interface NeutralinoType {
  os: {
    open: (url: string) => void;
  };
}

// Strip the trailing slash from the auth service URL
const AUTH_SERVICE_URL = import.meta.env.VITE_AUTH_SERVICE_URL.replace(
  /\/$/,
  "",
);

console.log("AUTH_SERVICE_URL", AUTH_SERVICE_URL);

export const ZoomIntegration = () => {
  const { toast } = useToast();
  const [hasAttemptedConnection, setHasAttemptedConnection] = useState(false);

  // Since we don't have integration status tracking in the unified auth system,
  // we'll default to not connected for now
  const isZoomConnected = false;

  const handleConnect = useCallback(() => {
    setHasAttemptedConnection(true);
  }, []);

  const handleManage = useCallback(() => {
    setHasAttemptedConnection(false);
  }, []);

  useEffect(() => {
    if (hasAttemptedConnection && isZoomConnected) {
      toast({
        title: "Zoom connected",
        description: "You have successfully connected your Zoom account.",
        variant: "default",
      });
      setHasAttemptedConnection(false);
    }
  }, [hasAttemptedConnection, toast]);

  return (
    <Card>
      <CardHeader>
        <div className="flex w-full flex-row">
          <div className="flex-grow font-semibold">Zoom</div>
          <div className="flex-grow-0">
            <IntegrationConnectionStatus
              status={null}
              connected={isZoomConnected}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex w-full flex-col space-y-4">
          <div className="text-sm text-gray-400">
            To allow Frontier to join Zoom video calls for all users in your
            Zoom workspace, an admin of your Zoom workspace must install the
            Frontier app.
          </div>
          <div>
            {isZoomConnected ? (
              <ManageZoom onManage={handleManage} />
            ) : (
              <ConnectZoom onConnect={handleConnect} />
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const ManageZoom = ({ onManage }: { onManage: () => void }) => {
  const isDesktop = useIsDesktop();
  const handleManage = () => {
    const authUrl = `${AUTH_SERVICE_URL}/integrations/zoom`;

    if (isDesktop && window.Neutralino) {
      const neutralino = window.Neutralino as NeutralinoType;
      neutralino.os.open(authUrl);
    } else {
      window.open(authUrl, "_blank");
    }
    onManage();
  };
  return (
    <Button onClick={handleManage} variant="secondary">
      Manage
    </Button>
  );
};

export const ConnectZoom = ({ onConnect }: { onConnect: () => void }) => {
  const isDesktop = useIsDesktop();
  const [isConnecting, setIsConnecting] = useState(false);
  const handleConnect = () => {
    setIsConnecting(true);

    const authUrl = `${AUTH_SERVICE_URL}/integrations/zoom`;

    if (isDesktop && window.Neutralino) {
      const neutralino = window.Neutralino as NeutralinoType;
      neutralino.os.open(authUrl);
    } else {
      window.open(authUrl, "_blank");
    }
    onConnect();
  };

  return (
    <Button onClick={handleConnect} disabled={isConnecting} variant="default">
      Connect
    </Button>
  );
};
